import { reactive } from "vue";
import api_config from "@/config";
import { notify, dismissNotification } from "@/utils/notify";

/* Une seule carte de coupure à la fois : l'identifiant la remplace au lieu
   de l'empiler, même si vingt requêtes échouent d'un coup. */
const OFFLINE_CARD = "sf-network-offline";
const ONLINE_CARD = "sf-network-online";

const PROBE_INTERVAL = 6000;

const ANNOUNCE_THROTTLE = 1000;

export const network = reactive({
  /** Dernier état connu. Réactif : un écran peut s'y abonner. */
  online: true,
});

let announcedAt = 0;
let probeTimer = null;
let watching = false;

/** Le système affirme-t-il qu'aucun réseau n'est disponible ? */
export function isBrowserOffline() {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

/** État courant, tel que l'application le connaît. */
export function isOnline() {
  return network.online;
}


export function isOfflineError(error) {
  if (!error) return false;
  if (error.isNetworkError) return true;
  if (error.response) return false;
  if (error.code === "ERR_CANCELED") return false;
  return (
    error.code === "ERR_NETWORK" ||
    error.code === "ECONNABORTED" ||
    error.message === "Network Error" ||
    Boolean(error.request)
  );
}

/** Erreur rendue aux appelants quand la requête n'a même pas été tentée. */
export function offlineError(config) {
  const error = new Error("Network unavailable");
  error.name = "OfflineError";
  error.code = "ERR_OFFLINE";
  error.isNetworkError = true;
  error.config = config;
  return error;
}

/** Affiche — ou ramène — la carte d'avertissement. */
function announceOffline() {
  const now = Date.now();
  if (now - announcedAt < ANNOUNCE_THROTTLE) return;
  announcedAt = now;

  dismissNotification(ONLINE_CARD);
  notify({
    id: OFFLINE_CARD,
    /* Rouge : une coupure bloque tout envoi et toute lecture. Ce n'est pas
       une mise en garde, c'est un arrêt. */
    tone: "error",
    /* Pictogramme dessiné par la carte, faute d'équivalent dans la fonte
       d'icônes : un signal wifi barré d'un cercle d'interdiction. */
    icon: "no-wifi",
    title: "network.offline_title",
    text: "network.offline_text",
    hint: "network.offline_hint",
    /* Elle reste : la coupure dure, le message aussi. */
    timeout: 0,
  });
}

/**
 * Constate une coupure.
 *
 * @param {object} options
 * @param {boolean} options.announce  redire la coupure même si elle était
 *                                    déjà connue — c'est le cas d'une requête
 *                                    tentée sans connexion.
 */
export function markOffline(options) {
  const announce = !options || options.announce !== false;

  if (network.online) {
    network.online = false;
    startProbing();
  }
  if (announce) announceOffline();
}

/** Constate le retour de la connexion. */
export function markOnline() {
  if (network.online) return;

  network.online = true;
  stopProbing();
  announcedAt = 0;

  dismissNotification(OFFLINE_CARD);
  notify({
    id: ONLINE_CARD,
    tone: "success",
    icon: "bi-wifi",
    title: "network.online_title",
    text: "network.online_text",
    timeout: 4000,
  });
}

/**
 * Signale une requête qu'on n'a pas tentée, faute de connexion.
 *
 * Appelé par l'intercepteur axios : c'est ce qui remet la carte devant les
 * yeux si l'utilisateur l'avait fermée.
 */
export function reportBlockedRequest() {
  markOffline({ announce: true });
}

/**
 * Sonde l'API pour voir si elle redevient joignable.
 *
 * `no-cors` suffit : on ne lit pas la réponse, seul compte le fait que la
 * requête aboutisse. Le paramètre horodaté écarte tout cache intermédiaire,
 * qui répondrait « joignable » sans que rien ne soit sorti de la machine.
 */
async function probe() {
  if (isBrowserOffline()) return false;
  try {
    await fetch(`${api_config.API_ENDPOINT}/api/system_config/?_=${Date.now()}`, {
      mode: "no-cors",
      cache: "no-store",
    });
    return true;
  } catch (error) {
    return false;
  }
}

function startProbing() {
  if (probeTimer) return;
  probeTimer = setInterval(async () => {
    if (await probe()) markOnline();
  }, PROBE_INTERVAL);
}

function stopProbing() {
  if (!probeTimer) return;
  clearInterval(probeTimer);
  probeTimer = null;
}

/**
 * Met la surveillance en place. À appeler une fois, au démarrage.
 *
 * Le retour du réseau n'est pas cru sur parole : `online` signifie qu'une
 * carte réseau est active, pas qu'internet est joignable. On sonde avant
 * d'annoncer la bonne nouvelle.
 */
export function installNetworkWatch() {
  if (watching || typeof window === "undefined") return;
  watching = true;

  window.addEventListener("offline", () => markOffline({ announce: true }));
  window.addEventListener("online", async () => {
    if (await probe()) markOnline();
  });

  if (isBrowserOffline()) markOffline({ announce: true });
}
